import { isNativeApp } from './platform';

// data: URL → Blob without fetch() (fetch on data: URLs is flaky in the WebView).
const dataUrlToBlob = (dataUrl) => {
  const [head, b64] = String(dataUrl).split(',');
  const mime = (head.match(/data:(.*?)(;|$)/) || [])[1] || 'image/jpeg';
  const bin = atob(b64 || '');
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const isCancel = (e) => {
  const msg = String(e?.message || e || '').toLowerCase();
  return msg.includes('cancel') || msg.includes('no image');
};

/**
 * Native camera / gallery picker via @capacitor/camera.
 *
 *   source:    'camera' opens the device camera straight away;
 *              'photos' opens the gallery;
 *              'prompt' shows a native "Camera / Photos" chooser.
 *   direction: 'front' | 'rear' (camera only).
 *
 * Returns a Blob, or `null` when the user cancels OR when we're not in the
 * native app (the caller keeps a hidden <input type=file> for the web path).
 * Throws only on a real failure so the caller can show an error + fall back.
 *
 * The `<input type="file" capture>` route does not reliably open the camera
 * inside an Android WebView — it usually just shows the file/gallery picker —
 * which is why this plugin path exists.
 */
export const capturePhoto = async ({ source = 'camera', direction = 'rear' } = {}) => {
  if (!isNativeApp()) return null;
  let mod;
  try {
    mod = await import('@capacitor/camera');
  } catch {
    return null; // plugin not bundled (web build) — caller falls back
  }
  const { Camera, CameraResultType, CameraSource, CameraDirection } = mod;
  if (!Camera?.getPhoto) return null;
  const src = source === 'photos'
    ? CameraSource.Photos
    : source === 'prompt'
      ? CameraSource.Prompt
      : CameraSource.Camera;
  try {
    const photo = await Camera.getPhoto({
      quality: 82,
      allowEditing: false,
      correctOrientation: true,
      saveToGallery: false,
      resultType: CameraResultType.DataUrl,
      source: src,
      direction: direction === 'front' ? CameraDirection.Front : CameraDirection.Rear,
      width: 1280,
    });
    if (!photo?.dataUrl) return null;
    return dataUrlToBlob(photo.dataUrl);
  } catch (e) {
    if (isCancel(e)) return null;
    throw e;
  }
};
